/**
 * Shared shell for every authenticated page: top navigation with the
 * venue identity, role-aware nav links (Admin links only render for
 * admins — UI-layer mirror of the RLS boundary), and a sign-out action.
 *
 * Below the sm breakpoint, the inline nav links collapse into a single
 * "Menu" button revealing a dropdown — without this, Templates and every
 * Admin page would simply be unreachable on a phone or narrow tablet,
 * since the inline nav was hidden there with nothing replacing it.
 */

import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';

export function AppLayout({ children }: { children: ReactNode }) {
  const { profile, role, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-ivory-100">
      <header className="border-b border-ivory-300 bg-evergreen-700">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-baseline gap-2">
              <span className="font-display text-lg font-medium text-ivory-50">
                The Pines Planner
              </span>
            </Link>

            <nav className="hidden items-center gap-6 sm:flex">
              <Link
                to="/"
                className="font-body text-sm text-ivory-200 transition-colors hover:text-ivory-50"
              >
                Weddings
              </Link>
              <Link
                to="/templates"
                className="font-body text-sm text-ivory-200 transition-colors hover:text-ivory-50"
              >
                Templates
              </Link>
              {role === 'admin' && (
                <>
                  <span className="text-ivory-300/40">|</span>
                  <Link
                    to="/admin/inventory"
                    className="font-body text-sm text-gold-200 transition-colors hover:text-gold-100"
                  >
                    Inventory
                  </Link>
                  <Link
                    to="/admin/venue-spaces"
                    className="font-body text-sm text-gold-200 transition-colors hover:text-gold-100"
                  >
                    Venue Spaces
                  </Link>
                  <Link
                    to="/admin/users"
                    className="font-body text-sm text-gold-200 transition-colors hover:text-gold-100"
                  >
                    Users
                  </Link>
                </>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <span className="hidden font-body text-sm text-ivory-200 sm:inline">
              {profile?.fullName ?? '\u2026'}
            </span>
            <button
              onClick={handleSignOut}
              className="hidden font-body text-sm text-ivory-300 underline-offset-2 transition-colors hover:text-ivory-50 hover:underline sm:inline"
            >
              Sign out
            </button>

            {/* Mobile/narrow-tablet menu button — the only way to reach
                Templates or any Admin page below the sm breakpoint. */}
            <button
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
              className="rounded p-1.5 font-body text-sm text-ivory-100 hover:bg-evergreen-600 sm:hidden"
            >
              {mobileMenuOpen ? '\u2715' : '\u2630'} Menu
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav className="flex flex-col gap-1 border-t border-evergreen-600 bg-evergreen-700 px-6 py-3 sm:hidden">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="rounded px-2 py-2 font-body text-sm text-ivory-200 hover:bg-evergreen-600"
            >
              Weddings
            </Link>
            <Link
              to="/templates"
              onClick={() => setMobileMenuOpen(false)}
              className="rounded px-2 py-2 font-body text-sm text-ivory-200 hover:bg-evergreen-600"
            >
              Templates
            </Link>
            {role === 'admin' && (
              <>
                <div className="my-1 border-t border-evergreen-600" />
                <Link
                  to="/admin/inventory"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded px-2 py-2 font-body text-sm text-gold-200 hover:bg-evergreen-600"
                >
                  Inventory
                </Link>
                <Link
                  to="/admin/venue-spaces"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded px-2 py-2 font-body text-sm text-gold-200 hover:bg-evergreen-600"
                >
                  Venue Spaces
                </Link>
                <Link
                  to="/admin/users"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded px-2 py-2 font-body text-sm text-gold-200 hover:bg-evergreen-600"
                >
                  Users
                </Link>
              </>
            )}
            <div className="my-1 border-t border-evergreen-600" />
            <div className="flex items-center justify-between px-2 py-1">
              <span className="font-body text-sm text-ivory-200">{profile?.fullName ?? '\u2026'}</span>
              <button
                onClick={handleSignOut}
                className="font-body text-sm text-ivory-300 underline-offset-2 hover:text-ivory-50 hover:underline"
              >
                Sign out
              </button>
            </div>
          </nav>
        )}
      </header>

      <main>{children}</main>
    </div>
  );
}
